import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ApiError,
  getUserFacingApiMessage,
  getLocalUserFacingApiMessage,
  passThroughMessage,
} from "@/lib/api-client";
import { Sentry } from "@/lib/sentry";
import { createOrderErrorMessage } from "@/features/marketplace/lib/order-errors";
import i18n from "@/lib/i18n";
import {
  fetchVendors,
  fetchVendorDetails,
  fetchConnections,
  createConnection,
  updateAnswers,
  resignConnection,
  disconnectVendor,
  fetchOrders,
  fetchOrder,
  fetchSentScripts,
  fetchOrderHandoffDefaults,
  previewOrderHandoffEmail,
  createOrder,
  retryOrder,
  updateOrderEta,
  updateOrderFulfillment,
  fetchAdminOrders,
  updateOrderScriptFulfillment,
  fetchPartnerOrders,
  uploadPartnerPreflight,
  enablePartnerLogin,
  createOpportunityCustomBrief,
  fetchOrderScriptPreflightComments,
  fetchPartnerOrderScriptPreflightComments,
  fetchMarketplaceOrderScriptByScriptReviewId,
  addOrderScriptPreflightComment,
  addPartnerOrderScriptNote,
} from "@/features/marketplace/api/marketplace.api";
import type {
  CreateConnectionPayload,
  UpdateAnswersPayload,
  ResignConnectionPayload,
  CreateOrderPayload,
  OrderHandoffEmailPreviewPayload,
  MarketplaceFulfillmentStatus,
  MarketplaceOrder,
  AdminOrdersFilters,
  TenantOrdersFilters,
  TenantOrdersPageResponse,
  PartnerOrdersFilter,
  PartnerOrdersQueryFilters,
  UpdateOrderScriptFulfillmentPayload,
  CreateCustomBriefPayload,
  MarketplaceOrderScriptByReviewLink,
} from "@/features/marketplace/types/marketplace.types";
import {
  fetchAdminVendors,
  fetchAdminVendor,
  createAdminVendor,
  replaceAdminVendor,
  activateAdminVendor,
  approveVendorContactEmail,
  deactivateAdminVendor,
  revokeVendorContactEmail,
  fetchPendingVendors,
  fetchPendingOrderHandoffs,
  linkPendingVendor,
  rejectPendingVendor,
  retryAdminOrderHandoff,
} from "@/features/admin/marketplace/api/admin-vendors.api";
import {
  fetchTeamRecipients,
  createTeamRecipient,
  updateTeamRecipient,
  deleteTeamRecipient,
} from "@/features/admin/marketplace/api/team-recipients.api";
import {
  fetchMarketplaceNotificationSettings,
  updateMarketplaceNotificationSettings,
} from "@/features/admin/marketplace/api/notification-settings.api";
import type {
  MarketplaceNotificationSettingsKey,
  MarketplaceNotificationSettingsRow,
} from "@/features/admin/marketplace/types/admin-marketplace.types";
import {
  acceptOrderImportWarnings,
  acceptPrefilledOrderImportDates,
  bulkSetOrderImportClientStartDates,
  bulkSetOrderImportNewVendorMapping,
  bulkSetOrderImportVendorMapping,
  commitOrderImport,
  fetchOrderImportPreview,
  parseOrderImportSpreadsheet,
  updateOrderImportRow,
} from "@/features/admin/marketplace/api/admin-order-import.api";
import type { CreativeImportPreviewDto } from "@nexus/contracts";
import { normalizeImportPreview } from "@/features/marketplace/admin/components/order-import/import-readiness";

import type { AdminVendorWritePayload } from "@/features/admin/marketplace/types/admin-marketplace.types";
import type {
  TeamRecipientCreatePayload,
  TeamRecipientUpdatePayload,
} from "@/features/admin/marketplace/types/admin-marketplace.types";
import type {
  BulkSetClientStartDateBody,
  BulkSetVendorMappingBody,
  BulkSetNewVendorMappingBody,
  UpdateCreativeImportRowBody,
} from "@nexus/contracts";
import {
  hasInFlightHandoffOrders,
  ORDER_HANDOFF_POLL_INTERVAL_MS,
} from "@/features/marketplace/lib/order-handoff-ux";
import { opportunityKeys } from "./opportunities.queries";
import { personaOpportunityScriptKeys } from "./persona-opportunity-scripts.queries";

import { marketplaceKeys, MARKETPLACE_STALE_TIME_MS } from "./marketplace-query-keys";

export function vendorsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.vendors(tid || "__none__"),
    queryFn: () => fetchVendors(tid),
    enabled: !!tid,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

/**
 * Vendor detail bundle (packages + questions) for the connect wizard.
 * Single round-trip covering step 2 (package picker) and step 3 (guidelines).
 */
export function vendorDetailsQueryOptions(vendorId: string | null | undefined) {
  const vid = vendorId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.vendorDetails(vid || "__none__"),
    queryFn: () => fetchVendorDetails(vid),
    enabled: !!vid,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function connectionsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.connections(tid || "__none__"),
    queryFn: () => fetchConnections(tid),
    enabled: !!tid,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function useConnectVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateConnectionPayload) => createConnection(payload),
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.connections(payload.tenantId),
      });
      toast.success(i18n.t("marketplace.toasts.connected", "Connected to partner"));
    },
  });
}

export function useUpdateAnswers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      connectionId: string;
      payload: UpdateAnswersPayload;
    }) => updateAnswers(args.connectionId, args.payload),
    onSuccess: (_data, args) => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.connections(args.payload.tenantId),
      });
      toast.success("Guidelines saved");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "TERMS_RESIGN_REQUIRED") {
        toast.error(
          i18n.t(
            "marketplace.toasts.termsUpdated",
            "Partner terms have been updated — please re-sign first",
          ),
        );
        return;
      }
      toast.error("Failed to save guidelines");
    },
    meta: { skipGlobalError: true },
  });
}

export function useResignConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      connectionId: string;
      payload: ResignConnectionPayload;
    }) => resignConnection(args.connectionId, args.payload),
    onSuccess: (_data, args) => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.connections(args.payload.tenantId),
      });
      toast.success("Terms signed");
    },
  });
}

export function useDisconnectVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { connectionId: string; tenantId: string }) =>
      disconnectVendor(args.connectionId, args.tenantId),
    onSuccess: (_data, args) => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.connections(args.tenantId),
      });
      toast.success(i18n.t("marketplace.toasts.disconnected", "Disconnected from partner"));
    },
  });
}


export function adminVendorsQueryOptions() {
  return queryOptions({
    queryKey: marketplaceKeys.adminVendors,
    queryFn: fetchAdminVendors,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function adminPendingVendorsQueryOptions() {
  return queryOptions({
    queryKey: marketplaceKeys.adminPendingVendors,
    queryFn: fetchPendingVendors,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function adminPendingHandoffsQueryOptions() {
  return queryOptions({
    queryKey: marketplaceKeys.adminPendingHandoffs,
    queryFn: fetchPendingOrderHandoffs,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function adminVendorQueryOptions(vendorId: string | null | undefined) {
  const vid = vendorId ?? "";
  return queryOptions({
    queryKey: marketplaceKeys.adminVendor(vid || "__none__"),
    queryFn: () => fetchAdminVendor(vid),
    enabled: !!vid,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

function invalidateMarketplaceCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  vendorId?: string,
) {
  queryClient.invalidateQueries({ queryKey: marketplaceKeys.adminVendors });
  queryClient.invalidateQueries({
    queryKey: marketplaceKeys.adminPendingVendors,
  });
  queryClient.invalidateQueries({
    queryKey: marketplaceKeys.adminPendingHandoffs,
  });
  if (vendorId) {
    queryClient.invalidateQueries({
      queryKey: marketplaceKeys.adminVendor(vendorId),
    });
    queryClient.invalidateQueries({
      queryKey: marketplaceKeys.vendorDetails(vendorId),
    });
  }
  // Invalidate the whole marketplace tree since catalog list is keyed by tenantId.
  queryClient.invalidateQueries({ queryKey: marketplaceKeys.all });
}

function isVendorWriteTransportError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === "OFFLINE" ||
      error.code === "NETWORK_ERROR" ||
      error.code === "TIMEOUT")
  );
}

function shouldReportVendorWriteError(error: unknown): boolean {
  if (isVendorWriteTransportError(error)) return false;
  if (error instanceof ApiError) return error.status >= 500;
  return true;
}

function toastVendorWriteError(err: unknown) {
  if (isVendorWriteTransportError(err)) return;
  if (shouldReportVendorWriteError(err)) {
    Sentry.captureException(err);
  }
  const message = getLocalUserFacingApiMessage(err, passThroughMessage);
  if (message) toast.error(message);
}

export function useCreateAdminVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AdminVendorWritePayload) =>
      createAdminVendor(payload),
    onSuccess: (data) => {
      invalidateMarketplaceCaches(queryClient, data.id);
      toast.success(i18n.t("marketplace.admin.toasts.created", "Partner created"));
    },
    onError: toastVendorWriteError,
    meta: { skipGlobalError: true },
  });
}

export function useReplaceAdminVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      vendorId: string;
      payload: AdminVendorWritePayload;
    }) => replaceAdminVendor(args.vendorId, args.payload),
    onSuccess: (data) => {
      invalidateMarketplaceCaches(queryClient, data.id);
      toast.success(i18n.t("marketplace.admin.toasts.saved", "Partner saved"));
    },
    onError: toastVendorWriteError,
    meta: { skipGlobalError: true },
  });
}

export function useToggleAdminVendorActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { vendorId: string; activate: boolean }) =>
      args.activate
        ? activateAdminVendor(args.vendorId)
        : deactivateAdminVendor(args.vendorId),
    onSuccess: (data, args) => {
      invalidateMarketplaceCaches(queryClient, data.id);
      toast.success(
        args.activate
          ? i18n.t("marketplace.admin.toasts.activated", "Partner activated")
          : i18n.t("marketplace.admin.toasts.deactivated", "Partner deactivated"),
      );
    },
  });
}

export function useSetVendorContactEmailApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      vendorId: string;
      emailId: string;
      approve: boolean;
    }) =>
      args.approve
        ? approveVendorContactEmail(args.vendorId, args.emailId)
        : revokeVendorContactEmail(args.vendorId, args.emailId),
    onSuccess: (_data, args) => {
      invalidateMarketplaceCaches(queryClient, args.vendorId);
      toast.success(args.approve ? "Address approved" : "Address revoked");
    },
    onError: (err) => {
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

export function useLinkPendingVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { pendingId: string; vendorId: string; tenantId?: string }) =>
      linkPendingVendor(args.pendingId, args.vendorId, args.tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminPendingVendors,
      });
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminPendingHandoffs,
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "marketplace" &&
          query.queryKey[1] === "admin" &&
          query.queryKey[2] === "order-import",
      });
      invalidateMarketplaceCaches(queryClient);
      toast.success(i18n.t("marketplace.admin.toasts.linked", "Partner linked"));
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useRejectPendingVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pendingId: string) => rejectPendingVendor(pendingId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminPendingVendors,
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "marketplace" &&
          query.queryKey[1] === "admin" &&
          query.queryKey[2] === "order-import",
      });
      toast.success(
        i18n.t("marketplace.admin.pending.cancelOnboardingSuccess"),
      );
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useRetryAdminOrderHandoff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => retryAdminOrderHandoff(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminPendingHandoffs,
      });
      queryClient.invalidateQueries({ queryKey: marketplaceKeys.all });
      toast.success(
        i18n.t("marketplace.admin.handoffs.retrySuccess", "Handoff re-queued"),
      );
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}


export function adminTeamRecipientsQueryOptions() {
  return queryOptions({
    queryKey: marketplaceKeys.adminTeamRecipients,
    queryFn: fetchTeamRecipients,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

function invalidateTeamRecipients(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({
    queryKey: marketplaceKeys.adminTeamRecipients,
  });
}

export function useCreateTeamRecipient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TeamRecipientCreatePayload) =>
      createTeamRecipient(payload),
    onSuccess: () => {
      invalidateTeamRecipients(queryClient);
      toast.success("Recipient added");
    },
    // Customer surface — duplicate 409 stays on the generic i18n message.
    onError: (err) => {
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

export function useUpdateTeamRecipient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      recipientId: string;
      payload: TeamRecipientUpdatePayload;
    }) => updateTeamRecipient(args.recipientId, args.payload),
    onSuccess: (data) => {
      invalidateTeamRecipients(queryClient);
      toast.success(data.isActive ? "Recipient resumed" : "Recipient paused");
    },
    onError: (err) => {
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

export function useDeleteTeamRecipient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recipientId: string) => deleteTeamRecipient(recipientId),
    onSuccess: () => {
      invalidateTeamRecipients(queryClient);
      toast.success("Recipient removed");
    },
  });
}

export function adminNotificationSettingsQueryOptions() {
  return queryOptions({
    queryKey: marketplaceKeys.adminNotificationSettings,
    queryFn: fetchMarketplaceNotificationSettings,
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function useUpdateMarketplaceNotificationSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Record<MarketplaceNotificationSettingsKey, boolean>>) =>
      updateMarketplaceNotificationSettings(patch),
    scope: { id: "marketplace-notification-settings" },
    onMutate: async (patch) => {
      await queryClient.cancelQueries({
        queryKey: marketplaceKeys.adminNotificationSettings,
      });
      const previous = queryClient.getQueryData<MarketplaceNotificationSettingsRow>(
        marketplaceKeys.adminNotificationSettings,
      );
      if (previous) {
        queryClient.setQueryData(marketplaceKeys.adminNotificationSettings, {
          ...previous,
          ...patch,
        });
      }
      return { previous };
    },
    onError: (err, _patch, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          marketplaceKeys.adminNotificationSettings,
          context.previous,
        );
      }
      toast.error(getUserFacingApiMessage(err));
    },
    onSuccess: (data) => {
      queryClient.setQueryData(marketplaceKeys.adminNotificationSettings, data);
    },
    meta: { skipGlobalError: true },
  });
}

